import type { VPError } from '../../VPError';

/**
 * Shared error classification for the internal (VP staff) schema.
 *
 * InternalGraphqlController rejects a caller it will not serve with HTTP 401 *before* the query
 * is parsed, and Apollo raises any status >= 300 as a `ServerError` rather than returning a
 * payload. Every `Internal/<Domain>/Api.ts` therefore has to catch and classify, so callers can
 * explain the rejection instead of rendering a broken page — and so an expected authorization
 * outcome does not land in Sentry as an exception.
 *
 * Two conditions share that 401, and `extensions.code` is the only thing that tells them apart:
 * a caller who is not staff (`UNAUTHORIZED`), and one whose session lapsed or never existed
 * (`UNAUTHENTICATED`). They need opposite answers — ask for access, or sign in again — so the
 * distinction is drawn here once rather than at every screen.
 */

/** `extensions.code` InternalGraphqlController sets when it rejects a non-staff caller. */
export const UNAUTHORIZED = 'UNAUTHORIZED';

/** `extensions.code` it sets when the session is absent or has lapsed. */
export const UNAUTHENTICATED = 'UNAUTHENTICATED';

export const GENERIC_MESSAGE = 'Request failed';

export type InternalApiError = VPError & { code?: string };

type ServerErrorBody = {
  errors?: Array<{ message?: string; extensions?: { code?: string } }>;
};

type NetworkErrorLike = {
  statusCode?: number;
  result?: ServerErrorBody;
};

type GraphQLErrorLike = {
  message: string;
  path?: ReadonlyArray<string | number>;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  return value as Record<string, unknown>;
};

// Apollo raises the controller's 401 as a `ServerError` (any status >= 300 throws
// before the body is inspected) and then wraps it as `networkError` on an
// `ApolloError` -- but a bare `ServerError` carries the same fields itself.
const networkErrorOf = (error: unknown): NetworkErrorLike | undefined => {
  const record = asRecord(error);
  if (!record) {
    return undefined;
  }

  return (asRecord(record.networkError) ?? record) as NetworkErrorLike;
};

const messageOf = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return GENERIC_MESSAGE;
};

const toApiError = (path: string, message: string, code?: string): InternalApiError =>
  code ? { path, message, code } : { path, message };

export const toApiErrors = (error: unknown): InternalApiError[] => {
  const networkError = networkErrorOf(error);
  const serverErrors = networkError?.result?.errors;
  // The controller always sends extensions.code, but this classification drives
  // what the caller renders -- don't let it hinge on a backend default. It stays
  // UNAUTHORIZED rather than becoming ambiguous: a 401 carrying no code is what a
  // backend that has not shipped the split answers, and the screens have always
  // read that as the staff gate.
  const codeFromStatus = networkError?.statusCode === 401 ? UNAUTHORIZED : undefined;

  if (serverErrors?.length) {
    return serverErrors.map(item => toApiError('', item.message ?? GENERIC_MESSAGE, item.extensions?.code ?? codeFromStatus));
  }

  if (codeFromStatus) {
    return [toApiError('', messageOf(error), codeFromStatus)];
  }

  const graphQLErrors = asRecord(error)?.graphQLErrors as GraphQLErrorLike[] | undefined;
  if (graphQLErrors?.length) {
    return graphQLErrors.map(item => toApiError((item.path ?? []).join('.'), item.message));
  }

  return [toApiError('', messageOf(error))];
};

export const isUnauthorized = (errors: InternalApiError[]): boolean =>
  errors.some(error => error.code === UNAUTHORIZED);

export const isUnauthenticated = (errors: InternalApiError[]): boolean =>
  errors.some(error => error.code === UNAUTHENTICATED);

/**
 * Either answer from the gate. What the Sentry guards branch on: both are expected outcomes, and
 * neither is an incident — a lapsed session is as ordinary as a caller who was never staff.
 */
export const isGateRejection = (errors: InternalApiError[]): boolean =>
  isUnauthorized(errors) || isUnauthenticated(errors);
